// src/utils/mapVoucherRowToPreview.js
//
// Adapter: backend voucher-domain row  →  <VoucherPreview /> "preview model".
// ===========================================================================
//
// WHY THIS EXISTS
// ----------------
// Your backend `/vouchers` endpoint returns a *domain object*
// (Sequelize row / API row with fields such as:
//
//   value
//   recipient_name
//   buyer_name
//   template_type
//   template_version
//   theme
//   category
//
// etc.)
//
// <VoucherPreview /> expects a *render model* shaped for frontend rendering,
// for example:
//
//   {
//     businessMode,
//     category,
//     type,
//
//     theme,
//     salonName,
//     logoUrl,
//
//     amount,
//     currency,
//
//     recipientName,
//     buyerName,
//     message,
//
//     expiresAtLabel?,
//     voucherNumberPlaceholder?,
//     qrPlaceholderLabel?,
//     templateType?,
//     templateVersion?,
//   }
//
// This helper is the single canonical place where we convert:
//
//   "voucher-domain row from backend"
//        into
//   "preview model for React UI"
//
// WHY THIS FILE IS NOW MORE IMPORTANT
// -----------------------------------
// Historically, the app could get away with treating this mapper as mostly
// voucher-only, because frontend preview and backend PDF logic were still
// close to a single-family mental model.
//
// That is no longer enough.
//
// The current architecture now explicitly distinguishes two business families:
//
//   1) voucher
//   2) package
//
// even though both are still stored in the broader vouchers domain/table.
//
// Therefore this mapper must now be BUSINESS-AWARE.
//
// In practice that means:
//
//   - category "Pakiet" must be understood as package mode,
//   - package rows may use a package-specific templateVersion catalog,
//   - when theme is missing, we may need to derive it differently,
//   - frontend preview should carry businessMode explicitly.
//
// BENEFITS
// --------
// - VoucherOverviewPage (admin preview) and ShopPage (public preview)
//   conceptually share the same preview field semantics.
// - Admin preview no longer silently assumes that every row is a classic voucher.
// - If you later tweak how recipientName, amount, validity text, or
//   package/voucher theme derivation works, you only change this file.
//
// IMPORTANT SCOPE BOUNDARY
// ------------------------
// This helper is still a FRONTEND adapter only.
// It does NOT:
//
//   - generate PDFs,
//   - decide React-PDF template routing,
//   - know anything about Sequelize queries,
//   - know anything about server filesystem assets.
//
// Backend has its sibling canonical mapper in:
//
//   backend/pdf/generateVoucherPdfBuffer.js
//
// and backend family routing happens later in:
//
//   backend/pdf/VoucherDocument.js
//
// This file simply prepares the browser-side preview model.
//

import { voucherLayoutConfig } from "../config/voucherLayoutConfig";
import { packageLayoutConfig } from "../config/packageLayoutConfig";

// ---------------------------------------------------------------------------
// BUSINESS MODE HELPERS
// ---------------------------------------------------------------------------

/**
 * Canonical frontend business modes.
 *
 * We keep these as plain strings because:
 * - they are easy to inspect in logs/devtools,
 * - they are easy to pass through preview models,
 * - they mirror the backend PDF routing concept.
 */
const PREVIEW_BUSINESS_MODES = Object.freeze({
  VOUCHER: "voucher",
  PACKAGE: "package",
});

/**
 * getBusinessModeFromVoucherRow(row)
 *
 * Resolve the business mode for a voucher-domain row.
 *
 * Preferred resolution order:
 *   1) explicit row.businessMode if present and valid
 *   2) category === "Pakiet" => package
 *   3) fallback => voucher
 *
 * Why this order?
 * ---------------
 * Because during migration some API responses may already provide an explicit
 * businessMode, while older ones may still only expose category.
 *
 * This helper keeps the mapper resilient during refactor.
 */
function getBusinessModeFromVoucherRow(row) {
  const explicit = String(row?.businessMode || "")
    .trim()
    .toLowerCase();

  if (explicit === PREVIEW_BUSINESS_MODES.PACKAGE) {
    return PREVIEW_BUSINESS_MODES.PACKAGE;
  }

  if (explicit === PREVIEW_BUSINESS_MODES.VOUCHER) {
    return PREVIEW_BUSINESS_MODES.VOUCHER;
  }

  return row?.category === "Pakiet"
    ? PREVIEW_BUSINESS_MODES.PACKAGE
    : PREVIEW_BUSINESS_MODES.VOUCHER;
}

/**
 * getLayoutConfigForBusinessMode(mode)
 *
 * Select the correct frontend layout config for the given business mode.
 *
 * IMPORTANT:
 * ----------
 * - voucherLayoutConfig remains the correct config for classic vouchers.
 * - packageLayoutConfig should mirror the same contract shape, but may have:
 *     - different templateVersion mapping
 *     - different defaults
 *     - future package-specific preview semantics
 *
 * Defensive fallback:
 *   - unknown mode => voucherLayoutConfig
 */
function getLayoutConfigForBusinessMode(mode) {
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? packageLayoutConfig || voucherLayoutConfig
    : voucherLayoutConfig;
}

// ---------------------------------------------------------------------------
// THEME NORMALIZATION / DERIVATION
// ---------------------------------------------------------------------------

/**
 * normalizeThemeForConfig(rawTheme, layoutConfig)
 *
 * Normalize a theme-like value into one canonical preview theme key that is
 * supported by the selected frontend config.
 *
 * Supported current canonical keys are typically:
 *   - default
 *   - christmas
 *   - valentine
 *
 * Legacy compatibility:
 *   - "warm" -> "valentine"
 *
 * Why keep normalization here even if VoucherPreview also normalizes?
 * ---------------------------------------------------------------
 * Because this mapper is supposed to be the canonical translation layer.
 * It is better if the preview model already contains a stable theme key
 * whenever possible.
 *
 * Still, VoucherPreview may normalize again defensively, which is fine.
 */
function normalizeThemeForConfig(rawTheme, layoutConfig) {
  const fallback = layoutConfig?.defaultThemeKey || "default";

  if (!rawTheme || typeof rawTheme !== "string") {
    return fallback;
  }

  const t = String(rawTheme).trim().toLowerCase();

  // Legacy frontend alias
  if (t === "warm") {
    return "valentine";
  }

  // Direct key hit in config.themes
  if (layoutConfig?.themes && layoutConfig.themes[t]) {
    return layoutConfig.themes[t]?.key || t;
  }

  // Some configs may expose color buckets by canonical key
  if (layoutConfig?.colors && layoutConfig.colors[t]) {
    return t;
  }

  return fallback;
}

/**
 * deriveThemeFromTemplateVersion(templateVersion, layoutConfig)
 *
 * Derive a theme from templateVersion using the selected frontend config.
 *
 * Preferred path:
 *   1) config.templateVersionToThemeMap[templateVersion]
 *   2) treat templateVersion itself as a possible theme-like key
 *   3) fallback to config.defaultThemeKey
 *
 * Why this matters now:
 * ---------------------
 * In the new architecture, package rows may legitimately have:
 *
 *   template_version = "package_classic" | "package_premium" | "package_gold"
 *
 * while theme may be empty.
 *
 * In that case the preview still needs a stable theme key.
 */
function deriveThemeFromTemplateVersion(templateVersion, layoutConfig) {
  const fallback = layoutConfig?.defaultThemeKey || "default";

  if (!templateVersion) return fallback;

  const v = String(templateVersion).trim().toLowerCase();

  const mapped = layoutConfig?.templateVersionToThemeMap?.[v];
  if (mapped) {
    return normalizeThemeForConfig(mapped, layoutConfig);
  }

  // Defensive fallback:
  // if templateVersion itself happens to already be a theme-like key
  // (e.g. "christmas"), normalize it.
  return normalizeThemeForConfig(v, layoutConfig);
}

// ---------------------------------------------------------------------------
// DATE / LABEL HELPERS
// ---------------------------------------------------------------------------

/**
 * buildExpiresAtLabel(voucher, businessMode)
 *
 * Build the preview validity label.
 *
 * Rule:
 *   - If expiration_date exists, build a concrete Polish label:
 *       "Bon ważny do 01.02.2026"
 *       "Pakiet ważny do 01.02.2026"
 *   - Otherwise return undefined so the preview component may fall back
 *     to the generic validity text from the selected layout config.
 *
 * IMPORTANT
 * ---------
 * This helper is no longer allowed to assume that one common noun ("Bon")
 * works for every preview family.
 *
 * After the voucher/package split:
 *   - voucher preview must use "Bon"
 *   - package preview must use "Pakiet"
 *
 * Why this belongs here
 * ---------------------
 * This mapper is the canonical frontend translation layer from:
 *
 *   backend voucher-domain row
 *        ->
 *   frontend preview model
 *
 * Therefore the preview model should already carry business-correct wording
 * instead of leaking voucher-only assumptions into package preview.
 *
 * @param {Object} voucher
 * @param {string} businessMode
 * @returns {string|undefined}
 */
function buildExpiresAtLabel(voucher, businessMode) {
  if (!voucher?.expiration_date) {
    return undefined;
  }

  const raw = String(voucher.expiration_date);

  // If backend sends YYYY-MM-DD (date-only), parse as local date
  // to avoid timezone shifts.
  const d =
    /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? new Date(
          Number(raw.slice(0, 4)),
          Number(raw.slice(5, 7)) - 1,
          Number(raw.slice(8, 10))
        )
      : new Date(raw);

  if (Number.isNaN(d.getTime())) {
    return undefined;
  }

  const labelNoun =
    businessMode === PREVIEW_BUSINESS_MODES.PACKAGE ? "Pakiet" : "Bon";

  return `${labelNoun} ważny do ${d.toLocaleDateString("pl-PL")}`;
}

// ---------------------------------------------------------------------------
// MAIN MAPPER
// ---------------------------------------------------------------------------

/**
 * mapVoucherRowToPreview(voucher)
 *
 * Map a backend voucher-domain row into the canonical preview model used by
 * <VoucherPreview />.
 *
 * @param {Object} voucher - A single voucher-domain row from the backend.
 * @returns {Object|null}  - Preview model or null if input is invalid.
 *
 * IMPORTANT CONTRACT NOTES
 * ------------------------
 * This mapper intentionally returns a slightly richer model than older
 * preview versions required.
 *
 * That is good.
 *
 * It means the preview layer can gradually evolve without every caller having
 * to rediscover the same business metadata.
 *
 * Current fields returned include:
 *
 *   - businessMode
 *   - category
 *   - type
 *   - theme
 *   - templateType
 *   - templateVersion
 *   - salonName
 *   - logoUrl
 *   - amount
 *   - currency
 *   - recipientName
 *   - buyerName
 *   - message
 *   - expiresAtLabel
 *   - voucherNumberPlaceholder
 */
export function mapVoucherRowToPreview(voucher) {
  if (!voucher || typeof voucher !== "object") {
    // Defensive: avoid throwing in calling components.
    return null;
  }

  // -------------------------------------------------------------------------
  // 1) Business mode + selected frontend config
  // -------------------------------------------------------------------------
  //
  // This is the core architectural update.
  //
  // We no longer blindly assume one voucher-only preview config.
  // Instead:
  //   - category/businessMode decides voucher vs package preview profile
  //   - that profile decides defaults and templateVersion -> theme mapping
  //
  const businessMode = getBusinessModeFromVoucherRow(voucher);
  const layoutConfig = getLayoutConfigForBusinessMode(businessMode);

  // -------------------------------------------------------------------------
  // 2) Template metadata
  // -------------------------------------------------------------------------
  //
  // We preserve these fields in the preview model because:
  //   - admin preview modal may want to show them,
  //   - VoucherPreview may later branch on them,
  //   - they directly mirror backend PDF routing concepts.
  //
  const templateType =
    voucher.template_type ||
    voucher.templateType ||
    "A4_ON_STATIONERY";

  const templateVersion =
    voucher.template_version ||
    voucher.templateVersion ||
    null;

  // -------------------------------------------------------------------------
  // 3) Theme
  // -------------------------------------------------------------------------
  //
  // Priority:
  //   1) explicit voucher.theme from backend row
  //   2) derive from templateVersion using selected config
  //   3) fallback to config default
  //
  // This is especially important for packages, because package motif/version
  // values may not be classic voucher motif names.
  //
  const theme = voucher.theme
    ? normalizeThemeForConfig(voucher.theme, layoutConfig)
    : deriveThemeFromTemplateVersion(templateVersion, layoutConfig);

  // -------------------------------------------------------------------------
  // 4) Amount + currency
  // -------------------------------------------------------------------------
  //
  // - `value` is the nominal row amount (major units, e.g. 150.00).
  // - `currency` is stored on the row (default "PLN").
  //
  const amount = Number(voucher.value);
  const currency = voucher.currency || "PLN";

  // If amount is not a valid number, keep it as 0 so preview stays robust.
  const safeAmount = Number.isFinite(amount) ? amount : 0;

  // -------------------------------------------------------------------------
  // 5) Recipient + buyer
  // -------------------------------------------------------------------------
  //
  // Data model after recent changes:
  //   - buyer_name:     full name of buyer (string or null)
  //   - recipient_name: full name of recipient (string or null)
  //
  // Display policy:
  //
  // Recipient:
  //   - prefer recipient_name
  //   - fallback to buyer_name only as defensive UI fallback
  //
  // Buyer:
  //   - use buyer_name as-is if present
  //
  // Why still allow recipient fallback to buyer?
  // -------------------------------------------
  // Because older rows or partially migrated rows may still omit recipient_name,
  // and preview should remain stable.
  //
  // For package flow, recipient_name is expected to be required earlier in the
  // create/edit UI, but this mapper still remains defensive.
  //
  const buyerName =
    voucher.buyer_name && String(voucher.buyer_name).trim()
      ? String(voucher.buyer_name).trim()
      : "";

  const recipientName =
    voucher.recipient_name && String(voucher.recipient_name).trim()
      ? String(voucher.recipient_name).trim()
      : buyerName || "";

  // -------------------------------------------------------------------------
  // 6) Message / description
  // -------------------------------------------------------------------------
  //
  // Current business convention:
  //   - description is the main printable free-text field
  //   - legacy message may still exist in some rows / old flows
  //
  // For preview purposes:
  //   description -> preferred
  //   message     -> fallback
  //
  const message =
    (typeof voucher.description === "string" && voucher.description) ||
    (typeof voucher.message === "string" && voucher.message) ||
    "";

  // -------------------------------------------------------------------------
  // 7) Expiration label
  // -------------------------------------------------------------------------
  //
  // - If expiration_date is present, build a concrete label.
  // - Otherwise leave undefined so VoucherPreview can fall back to the
  //   generic validity text from selected layout config.
  //
  const expiresAtLabel = buildExpiresAtLabel(voucher, businessMode);

  // -------------------------------------------------------------------------
  // 8) Visible voucher number
  // -------------------------------------------------------------------------
  //
  // In admin preview we show the real voucher number.
  // ShopPage uses its own pre-purchase placeholder.
  //
  // We keep the older prop name `voucherNumberPlaceholder` because
  // VoucherPreview already expects it.
  //
  const voucherNumberPlaceholder = voucher.voucher_no || "—";

  // -------------------------------------------------------------------------
  // 9) Salon name + logo
  // -------------------------------------------------------------------------
  //
  // - We read salonName from the selected layout config.
  // - Logo remains the same static frontend asset for both families.
  //
  // IMPORTANT:
  // ----------
  // Even if packages later receive a slightly different visual treatment,
  // using the same shared salon branding asset is still perfectly valid.
  //
  const salonName =
    layoutConfig?.salonName || "HB Health & Beauty";

  const logoUrl = `${process.env.PUBLIC_URL || ""}/hb_logo.png`;

  // -------------------------------------------------------------------------
  // 10) Return canonical preview model
  // -------------------------------------------------------------------------
  //
  // NOTE:
  // - `qrPlaceholderLabel` is intentionally omitted so VoucherPreview may use
  //   its own default.
  // - We now return business metadata too, not only classic visual props.
  // - This richer model helps VoucherOverviewPage and future preview logic
  //   stay aligned with backend PDF routing concepts.
  //
  return {
    // -----------------------------------------------------------------------
    // Core business identity
    // -----------------------------------------------------------------------
    businessMode,
    category: voucher.category || null,
    type: voucher.type || null,

    // -----------------------------------------------------------------------
    // Layout metadata
    // -----------------------------------------------------------------------
    theme,
    templateType,
    templateVersion,

    // -----------------------------------------------------------------------
    // Branding
    // -----------------------------------------------------------------------
    salonName,
    logoUrl,

    // -----------------------------------------------------------------------
    // Financial
    // -----------------------------------------------------------------------
    amount: safeAmount,
    currency,

    // -----------------------------------------------------------------------
    // People / message
    // -----------------------------------------------------------------------
    recipientName,
    buyerName,
    message,

    // -----------------------------------------------------------------------
    // Optional labels / placeholders
    // -----------------------------------------------------------------------
    expiresAtLabel, // optional – VoucherPreview may fallback if undefined
    voucherNumberPlaceholder,
  };
}